/**
 * Range key for the analytics charts — kept short so the same key string can
 * be passed verbatim to the API as `?from=` / `?to=` shortcuts via the
 * resolveRange helper there.
 */
export type AnalyticsRangeKey = '1h' | '24h' | '7d' | '30d';

/**
 * Centralised TanStack Query key factory. Group invalidations by passing a prefix.
 */
export const qk = {
  me: () => ['me'] as const,
  locales: () => ['locales'] as const,

  // posts
  posts: (params: object) => ['posts', params] as const,
  post: (slug: string, locale: string) => ['post', slug, locale] as const,

  // categories / tags / authors
  categories: (locale: string) => ['categories', locale] as const,
  tags: (locale: string) => ['tags', locale] as const,
  authors: (locale: string) => ['authors', locale] as const,

  // home / trending
  home: (locale: string) => ['home', locale] as const,
  trending: (window: string, locale: string) => ['trending', window, locale] as const,

  // channels
  channels: (admin: boolean) => ['channels', { admin }] as const,
  channel: (slug: string) => ['channel', slug] as const,
  nowPlaying: (slug: string, locale: string) => ['now-playing', slug, locale] as const,

  // programmes
  programmes: (params: object) => ['programmes', params] as const,
  programme: (slug: string, locale: string) => ['programme', slug, locale] as const,

  // episodes
  episodes: (programmeId: string, params: object) => ['episodes', programmeId, params] as const,
  episode: (programmeId: string, episodeSlug: string, locale: string) =>
    ['episode', programmeId, episodeSlug, locale] as const,

  // playlists
  playlists: () => ['playlists'] as const,
  playlist: (id: string) => ['playlist', id] as const,

  // schedule
  scheduleDay: (channel: string, locale: string, date: string) =>
    ['schedule-day', channel, locale, date] as const,
  scheduleWeek: (channel: string, locale: string, weekStart: string) =>
    ['schedule-week', channel, locale, weekStart] as const,

  // events
  events: (params: object) => ['events', params] as const,
  event: (slug: string, locale: string) => ['event', slug, locale] as const,

  // users (admin)
  users: (params: object) => ['users', params] as const,
  user: (id: string) => ['user', id] as const,

  // youtube live status — short TTL via tanstack refetchInterval
  youtubeLive: (programmeId: string) => ['youtube-live', programmeId] as const,

  // activity log
  activity: (params: object) => ['activity', params] as const,
  activitySummary: (sinceHours: number) => ['activity-summary', sinceHours] as const,

  // analytics — keyed by entity + range so range-flips don't trash other panels
  analyticsOverview: () => ['analytics-overview'] as const,
  analyticsListeners: (slug: string, range: AnalyticsRangeKey) =>
    ['analytics-listeners', slug, range] as const,
  analyticsLiveViewers: (slug: string, range: AnalyticsRangeKey) =>
    ['analytics-live-viewers', slug, range] as const,
  analyticsEpisodeViews: (id: string, range: AnalyticsRangeKey) =>
    ['analytics-episode-views', id, range] as const,
  // Phase 3 client-reported events
  analyticsPostViews: (range: AnalyticsRangeKey) => ['analytics-post-views', range] as const,
  analyticsTopPosts: (range: AnalyticsRangeKey) => ['analytics-top-posts', range] as const,
  analyticsSessions: (range: AnalyticsRangeKey) => ['analytics-sessions', range] as const,
  analyticsPushCtr: (range: AnalyticsRangeKey) => ['analytics-push-ctr', range] as const,
  analyticsPlayTime: (range: AnalyticsRangeKey, scopeType?: string, scopeId?: string) =>
    ['analytics-play-time', range, scopeType ?? null, scopeId ?? null] as const,
  analyticsGeo: (days: number, locale?: string) =>
    ['analytics-geo', days, locale ?? null] as const,
  analyticsSessionsHeatmap: (days: number) =>
    ['analytics-sessions-heatmap', days] as const,
  analyticsPushSendTimeCtr: (days: number) =>
    ['analytics-push-send-time-ctr', days] as const,
  analyticsDeviceBreakdown: (days: number) =>
    ['analytics-device-breakdown', days] as const,

  // editorial inbox
  inbox: () => ['inbox'] as const,
};
